import { computeDefaultTargetDate } from '~/pages/DemandForecastList/models/tabDefinitions'

/**
 * 対象日 radio (design/demand/forecast-list.html 2026-07-15 ユーザー指示 / 7/16
 * design-desk 同期版): 単日タブの date picker + `<` `>` と対象日 (state) を
 * 双方向連動させるための区分。
 * - computeTargetDateSegment: 現在の対象日 → どの radio を active にするか
 * - targetDateForSegment: radio クリック → 対象日を何に設定するか
 */
export type TargetDateSegmentKey =
  | 'today'
  | 'tomorrow'
  | 'dayAfterTomorrow'
  | 'beyond'

/** design segmented の表示順 (design HTML L114-118)。 */
export const TARGET_DATE_SEGMENTS: readonly TargetDateSegmentKey[] = [
  'today',
  'tomorrow',
  'dayAfterTomorrow',
  'beyond',
]

/** i18n key (page:demandForecastList.filter.targetDateSegment.<key>)。 */
export const TARGET_DATE_SEGMENT_I18N_KEY: Record<
  TargetDateSegmentKey,
  string
> = {
  today: 'page:demandForecastList.filter.targetDateSegment.today',
  tomorrow: 'page:demandForecastList.filter.targetDateSegment.tomorrow',
  dayAfterTomorrow:
    'page:demandForecastList.filter.targetDateSegment.dayAfterTomorrow',
  beyond: 'page:demandForecastList.filter.targetDateSegment.beyond',
}

const SEGMENT_OFFSET: Record<
  Exclude<TargetDateSegmentKey, 'beyond'>,
  number
> = {
  today: 0,
  tomorrow: 1,
  dayAfterTomorrow: 2,
}

/** 「翌々日以降」を直接クリックした時に設定するオフセット (今日+3日、design 2026-07-15 ユーザー指示)。 */
const BEYOND_OFFSET = 3

/** 2 つの `Date` (ローカル日) 間の日数差。 a が b より未来なら正。 */
function diffInCalendarDays(a: Date, b: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((startOfDay(a) - startOfDay(b)) / msPerDay)
}

/**
 * 対象日 (targetDate) が今日からのオフセットで見てどの区分に該当するかを判定する
 * (date picker / `<` `>` で対象日を変えた時に radio の active を同期させるために使う)。
 * 過去日 (オフセット < 0) はどの区分にも該当しない (= radio 全て非 active、design
 * コメント「対象日が当日より前 (過去) = 枠外」に対応)。
 */
export function computeTargetDateSegment(
  targetDate: Date,
  today: Date,
): TargetDateSegmentKey | undefined {
  const offset = diffInCalendarDays(targetDate, today)
  if (offset === SEGMENT_OFFSET.today) return 'today'
  if (offset === SEGMENT_OFFSET.tomorrow) return 'tomorrow'
  if (offset === SEGMENT_OFFSET.dayAfterTomorrow) return 'dayAfterTomorrow'
  if (offset >= BEYOND_OFFSET) return 'beyond'
  return undefined
}

/**
 * 区分 (radio クリック) から対象日を作る。「翌々日以降」は今日+3日固定
 * (design 2026-07-15 ユーザー指示: 直接クリックで当日+3日を表示)。
 */
export function targetDateForSegment(
  segment: TargetDateSegmentKey,
  today: Date,
): Date {
  const offset = segment === 'beyond' ? BEYOND_OFFSET : SEGMENT_OFFSET[segment]
  return computeDefaultTargetDate(today, offset)
}
